import {
  EditorTools,
  EditorUtils,
  getHtml,
  ProseMirror,
} from '@progress/kendo-react-editor';
import { UploadFileInfo, UploadFileStatus } from '@progress/kendo-react-upload';
import { produce } from 'immer';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import Editor from '../../../../../../common/widgets/Editor';
import FileInput from '../../../../../../common/widgets/FileInput';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import RadioGroup from '../../../../../../common/widgets/RadioGroup';
import TextArea from '../../../../../../common/widgets/TextArea';
import { ErrorState } from '../../../../../benefit/models/Errors';
import { ProductLocationTypes } from '../../../../enums';
import { CountyFilterModel } from '../../../../models/filters';
import { resources } from '../../../../resources/resources';
import { productsService } from '../../../../services/products';
import {
  addressField,
  brandDescriptionField,
  descriptionField,
  descriptionInitialValue,
  emailField,
  howToOrderField,
  nameField,
  phoneNumberField,
  websiteField,
} from '../constants';
import { ProductPreview } from '../ProductPreview';
import { ProductPreviewData } from '../types';
import {
  getRoField,
  getRoName,
  ProductDetailsForm,
  setEditorFontOnMount,
} from '../utils';
import { AddLocationDialog } from './Location/AddLocationDialog';
import { EditLocationDialog } from './Location/EditLocationDialog';

interface ProductDetailsProps {
  previewData?: ProductPreviewData;
  productId?: number;
  countiesWithCities: CountyFilterModel[];
  detailsForm: ProductDetailsForm;
  isSaveButtonLoading?: boolean;
  errors: ErrorState;
  setErrors: (errors: ErrorState) => void;
  setDetailsForm: (form: ProductDetailsForm) => void;
  buttonFunctions?: {
    onBackClick: () => void;
    onContinueClick: () => void;
  };
  showContactDetailsFields: boolean;
}

const { Bold, Italic, Underline, Strikethrough, UnorderedList, OrderedList } =
  EditorTools;

export const ProductDetails = (props: ProductDetailsProps) => {
  const {
    productId,
    previewData,
    countiesWithCities,
    detailsForm,
    isSaveButtonLoading,
    errors,
    setErrors,
    setDetailsForm,
    buttonFunctions,
    showContactDetailsFields,
  } = props;
  const { t: translate } = useTranslation();
  const [files, setFiles] = useState<UploadFileInfo[]>(detailsForm.files ?? []);
  const [showAddLocationDialog, setShowAddLocationDialog] =
    useState<boolean>(false);
  const [descriptionEditorValue, setDescriptionEditorValue] = useState(
    EditorUtils.createDocument(
      new ProseMirror.Schema({
        nodes: EditorUtils.nodes,
        marks: EditorUtils.marks,
      }),
      detailsForm.productDescriptions.ro.description?.value ??
        descriptionInitialValue,
    ),
  );
  const [selectedEditCounty, setSelectedEditCounty] = useState<
    CountyFilterModel | undefined
  >();

  const clearErrors = (field: string) => {
    setErrors(
      produce(errors, d => {
        d[field] = [];
      }),
    );
  };

  const deleteSelectedCounty = (e: any) => {
    setDetailsForm(
      produce(detailsForm, d => {
        d.selectedCountiesWithCities =
          d.selectedCountiesWithCities?.filter(
            c => c.filterId !== e.filterId,
          ) ?? [];
      }),
    );
  };

  return (
    <>
      {previewData && (
        <ProductPreview
          productTypes={previewData.productTypes}
          producyType={previewData.producyType}
          productStatuses={previewData.productStatuses}
          status={previewData.status}
          name={detailsForm.productDescriptions.ro.name?.value}
          imageContent={detailsForm.imageContent}
          showPricePreview
        />
      )}
      <div className="m-panel">
        <h1>{translate(resources.products.add.productDetails.title)}</h1>
        <div className="m-form">
          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(resources.products.add.productDetails.name.label)}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Input
                value={detailsForm.productDescriptions.ro.name?.value}
                onChange={e =>
                  setDetailsForm(
                    produce(detailsForm, d => {
                      d.productDescriptions.ro.name = getRoField(
                        e.value,
                        nameField,
                      );
                    }),
                  )
                }
                mandatory
                errors={errors[getRoName(nameField)]}
                clearErrors={() => clearErrors(getRoName(nameField))}
              />
              <span className="m-form-info">
                {translate(resources.products.add.productDetails.name.info)}
              </span>
            </div>
          </div>

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.productDetails.description.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Editor
                tools={[
                  Bold,
                  Italic,
                  Underline,
                  Strikethrough,
                  UnorderedList,
                  OrderedList,
                ]}
                value={descriptionEditorValue}
                onChange={e => {
                  setDescriptionEditorValue(e.value as any);
                  setDetailsForm(
                    produce(detailsForm, d => {
                      d.productDescriptions.ro.description = getRoField(
                        getHtml({
                          doc: e.value as any,
                        }),
                        descriptionField,
                      );
                    }),
                  );
                }}
                mandatory
                errors={errors[getRoName(descriptionField)]}
                clearErrors={() => clearErrors(getRoName(descriptionField))}
                onMount={setEditorFontOnMount}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.productDetails.description.info,
                )}
              </span>
            </div>
          </div>

          <hr className="m-form-separator" />

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(resources.products.add.productDetails.image.label)}
              </h4>
            </div>
            <div className="m-form-input">
              <FileInput
                className="m-upload m-img-upload"
                withCredentials={false}
                multiple={false}
                errors={errors.picture}
                files={files}
                saveUrl={async files => {
                  const allowedExtensions = ['.jpg', '.png'];
                  const maxFileSize = 1048576; // 1MB
                  const file = files[0];
                  const fileExtension = file.extension ?? '';
                  const fileSize = file.size ?? 0;
                  if (
                    !allowedExtensions.includes(fileExtension) ||
                    fileSize > maxFileSize
                  ) {
                    setErrors(
                      produce(errors, d => {
                        d.picture = [
                          {
                            code: resources.products.add.productDetails.image
                              .restrictions,
                            placeholders: {},
                            severity: 'error',
                            target: 'picture',
                          },
                        ];
                      }),
                    );
                    return { uid: file.uid };
                  } else {
                    setErrors(
                      produce(errors, d => {
                        d.picture = [];
                      }),
                    );
                  }
                  setFiles(files);
                  const rawFile = file.getRawFile!();

                  const formData = new FormData();
                  formData.append('file', rawFile);

                  const resp = await productsService.addProductImage(formData);
                  if (resp.hasErrors) {
                    showErrorToast(translate(resources.errors.generic));
                  } else {
                    const newFiles = files.map(e => ({
                      ...e,
                      status: UploadFileStatus.Uploaded,
                    }));
                    setFiles(newFiles);

                    const reader = new FileReader();
                    reader.readAsDataURL(rawFile);
                    reader.onload = () => {
                      setDetailsForm(
                        produce(detailsForm, d => {
                          d.image = resp.data;
                          d.imageContent = reader.result as string;
                          d.files = newFiles;
                        }),
                      );
                    };
                  }

                  return { uid: file.uid };
                }}
                removeUrl={async () => {
                  const resp = await productsService.removeProductImage(
                    detailsForm.image!,
                  );
                  if (resp.hasErrors) {
                    showErrorToast(translate(resources.errors.generic));
                  } else {
                    setFiles([]);
                    setDetailsForm(
                      produce(detailsForm, d => {
                        d.image = undefined;
                        d.imageContent = undefined;
                        d.files = [];
                      }),
                    );
                  }
                  return { uid: '' };
                }}
              />
              <span
                dangerouslySetInnerHTML={{
                  __html: translate(
                    resources.products.add.productDetails.image.info,
                  ),
                }}
                className="m-form-info"
              />
            </div>
          </div>

          <hr className="m-form-separator" />

          {showContactDetailsFields && (
            <>
              <h3>
                {translate(resources.products.add.productDetails.contact.title)}
              </h3>

              <div className="m-form-section">
                <div className="m-form-label">
                  <h4>
                    {translate(
                      resources.products.add.productDetails.contact
                        .brandDescription,
                    )}
                    {'*'}
                  </h4>
                </div>
                <div className="m-form-input">
                  <TextArea
                    value={
                      detailsForm.productDescriptions.ro.brandDescription
                        ?.value ?? ''
                    }
                    onChange={e =>
                      setDetailsForm(
                        produce(detailsForm, d => {
                          d.productDescriptions.ro.brandDescription =
                            getRoField(e.value, brandDescriptionField);
                        }),
                      )
                    }
                    mandatory
                    errors={errors[getRoName(brandDescriptionField)]}
                    clearErrors={() =>
                      clearErrors(getRoName(brandDescriptionField))
                    }
                    rows={5}
                  />
                </div>
              </div>

              <div className="m-form-section">
                <div className="m-form-label">
                  <h4>
                    {translate(
                      resources.products.add.productDetails.contact.contactInfo,
                    )}
                    {'*'}
                  </h4>
                </div>
                <div className="m-form-input">
                  <div className="row">
                    <div className="col col-6">
                      <Input
                        label={translate(
                          resources.products.add.productDetails.contact.address,
                        )}
                        value={
                          detailsForm.productDescriptions.ro.address?.value
                        }
                        onChange={e =>
                          setDetailsForm(
                            produce(detailsForm, d => {
                              d.productDescriptions.ro.address = getRoField(
                                e.value,
                                addressField,
                              );
                            }),
                          )
                        }
                        errors={errors[getRoName(addressField)]}
                        clearErrors={() => clearErrors(getRoName(addressField))}
                      />
                    </div>
                    <div className="col col-6">
                      <Input
                        label={translate(
                          resources.products.add.productDetails.contact
                            .phoneNumber,
                        )}
                        value={
                          detailsForm.productDescriptions.ro.phoneNumber?.value
                        }
                        onChange={e =>
                          setDetailsForm(
                            produce(detailsForm, d => {
                              d.productDescriptions.ro.phoneNumber = getRoField(
                                e.value,
                                phoneNumberField,
                              );
                            }),
                          )
                        }
                        errors={errors[getRoName(phoneNumberField)]}
                        clearErrors={() =>
                          clearErrors(getRoName(phoneNumberField))
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-6">
                      <Input
                        label={translate(
                          resources.products.add.productDetails.contact.email,
                        )}
                        value={detailsForm.productDescriptions.ro.email?.value}
                        onChange={e =>
                          setDetailsForm(
                            produce(detailsForm, d => {
                              d.productDescriptions.ro.email = getRoField(
                                e.value,
                                emailField,
                              );
                            }),
                          )
                        }
                        errors={errors[getRoName(emailField)]}
                        clearErrors={() => clearErrors(getRoName(emailField))}
                        controlClassName="mb-0"
                      />
                    </div>
                    <div className="col col-6">
                      <Input
                        label={translate(
                          resources.products.add.productDetails.contact.website,
                        )}
                        value={
                          detailsForm.productDescriptions.ro.website?.value
                        }
                        onChange={e =>
                          setDetailsForm(
                            produce(detailsForm, d => {
                              d.productDescriptions.ro.website = getRoField(
                                e.value,
                                websiteField,
                              );
                            }),
                          )
                        }
                        errors={errors[getRoName(websiteField)]}
                        clearErrors={() => clearErrors(getRoName(websiteField))}
                        controlClassName="mb-0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <hr className="m-form-separator" />
            </>
          )}

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.productDetails.howToOrder.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <TextArea
                value={
                  detailsForm.productDescriptions.ro.howToOrder?.value ?? ''
                }
                onChange={e =>
                  setDetailsForm(
                    produce(detailsForm, d => {
                      d.productDescriptions.ro.howToOrder = getRoField(
                        e.value,
                        howToOrderField,
                      );
                    }),
                  )
                }
                mandatory
                errors={errors[getRoName(howToOrderField)]}
                clearErrors={() => clearErrors(getRoName(howToOrderField))}
                rows={5}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.productDetails.howToOrder.info,
                )}
              </span>
            </div>
          </div>

          <hr className="m-form-separator" />

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.productDetails.location.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input m-form-margin-0">
              <span className="m-form-info top">
                {translate(resources.products.add.productDetails.location.info)}
              </span>
              <RadioGroup
                layout="horizontal"
                data={[
                  {
                    value: ProductLocationTypes.Online,
                    label: translate(
                      resources.products.add.productDetails.location.types
                        .online,
                    ),
                  },
                  {
                    value: ProductLocationTypes.SpecificLocations,
                    label: translate(
                      resources.products.add.productDetails.location.types
                        .specificLocations,
                    ),
                  },
                  {
                    value: ProductLocationTypes.OnlineAndSpecificLocations,
                    label: translate(
                      resources.products.add.productDetails.location.types
                        .onlineAndSpecificLocations,
                    ),
                  },
                ]}
                value={detailsForm.locationType}
                onChange={e => {
                  setDetailsForm(
                    produce(detailsForm, d => {
                      d.locationType = e.value;
                      if (e.value === ProductLocationTypes.Online) {
                        d.selectedCountiesWithCities = [];
                      }
                    }),
                  );
                  clearErrors('location');
                }}
                errors={errors.location}
              />
            </div>
          </div>

          {(detailsForm.locationType ===
            ProductLocationTypes.SpecificLocations ||
            detailsForm.locationType ===
              ProductLocationTypes.OnlineAndSpecificLocations) && (
            <div className="m-form-section">
              <div className="m-form-label">
                <h4>
                  {translate(
                    resources.products.add.productDetails.location.addLocations
                      .label,
                  )}
                  {'*'}
                </h4>
              </div>
              <div className="m-form-input">
                <span className="m-form-info top">
                  {translate(
                    resources.products.add.productDetails.location.addLocations
                      .info,
                  )}
                </span>
                <div className="locations-list">
                  {detailsForm.selectedCountiesWithCities?.map(e => (
                    <div className="locations-list-item" key={e.filterId}>
                      <span className="locations-list-icon">
                        <Icons.MLocationList />
                      </span>
                      <span className="locations-list-locations">
                        {translate(
                          resources.products.add.productDetails.location
                            .addLocations.countyWithCities,
                          {
                            0: e.name,
                            1: e.cities.map(e => e.name).join(', '),
                          },
                        )}
                      </span>
                      <span className="locations-list-actions">
                        <span
                          onClick={() => {
                            setSelectedEditCounty(e);
                          }}
                        >
                          <Icons.MLocationEdit />
                        </span>
                        <span
                          onClick={() => {
                            deleteSelectedCounty(e);
                          }}
                        >
                          <Icons.MLocationDelete />
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="add-location-wrapper">
                  <Button
                    onClick={() => setShowAddLocationDialog(true)}
                    className="btn btn-outline"
                    icon={<Icons.MAddLocation />}
                  >
                    <span className="btn-text">
                      {translate(
                        resources.products.add.productDetails.location
                          .addLocations.button,
                      )}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {buttonFunctions && (
        <div className="m-actions-panel">
          {_.isNil(productId) && (
            <Button
              onClick={buttonFunctions.onBackClick}
              className="btn btn-outline"
              disabled={isSaveButtonLoading}
            >
              <span className="btn-text">{translate(resources.back)}</span>
            </Button>
          )}
          <Button
            onClick={buttonFunctions.onContinueClick}
            className="btn btn-solid"
            loading={isSaveButtonLoading}
          >
            <span className="btn-text">
              {translate(resources.saveAndContinue)}
            </span>
          </Button>
        </div>
      )}
      {showAddLocationDialog && (
        <AddLocationDialog
          countiesWithCities={countiesWithCities}
          closeDialog={() => setShowAddLocationDialog(false)}
          selectedCountiesWithCities={
            detailsForm.selectedCountiesWithCities ?? []
          }
          addLocation={countyWithCities => {
            setDetailsForm(
              produce(detailsForm, d => {
                if (d.selectedCountiesWithCities) {
                  d.selectedCountiesWithCities.push(countyWithCities);
                } else {
                  d.selectedCountiesWithCities = [countyWithCities];
                }
              }),
            );
            setShowAddLocationDialog(false);
          }}
        />
      )}
      {selectedEditCounty && (
        <EditLocationDialog
          selectedEditCounty={selectedEditCounty}
          countyCities={
            countiesWithCities?.find(
              e => e.filterId === selectedEditCounty.filterId,
            )?.cities ?? []
          }
          closeDialog={() => setSelectedEditCounty(undefined)}
          editLocation={countyWithCities => {
            setDetailsForm(
              produce(detailsForm, d => {
                const county = d.selectedCountiesWithCities?.find(
                  e => e.filterId === countyWithCities.filterId,
                );
                if (!county) return;
                county.cities = [...countyWithCities.cities];
              }),
            );
            setSelectedEditCounty(undefined);
          }}
        />
      )}
    </>
  );
};
